import { formatBucket, isFullyLoanedOut, loanedCopies } from './formats';
import { EMPTY_LOCATIONS, movieIsAtLocation, type LocationBook } from './locations';
import { DISC_FORMATS, type DiscFormat, type Movie } from './types';
import type { Locale } from './i18n/index';

export type SortKey = 'title' | 'added' | 'releaseDate' | 'lastWatched' | 'watchCount' | 'runtime';
export type SortDirection = 'asc' | 'desc';
export type WatchState = 'all' | 'watched' | 'unwatched';
export type LoanState = 'all' | 'loaned' | 'shelf';

export interface CollectionFilter {
  query: string;
  /** OR-ed together. Empty means "any format". */
  formats: DiscFormat[];
  watchState: WatchState;
  loanState: LoanState;
  /** OR-ed together. Empty means "any genre". */
  genreIds: number[];
  /** OR-ed together. Empty means "anywhere". Resolved against the settings. */
  locationIds: string[];
  sort: SortKey;
  direction: SortDirection;
}

export const SORT_KEYS: readonly SortKey[] = [
  'title',
  'added',
  'releaseDate',
  'lastWatched',
  'watchCount',
  'runtime',
] as const;
export const SORT_DIRECTIONS: readonly SortDirection[] = ['asc', 'desc'] as const;
export const WATCH_STATES: readonly WatchState[] = ['all', 'watched', 'unwatched'] as const;
export const LOAN_STATES: readonly LoanState[] = ['all', 'loaned', 'shelf'] as const;

export const DEFAULT_FILTER: CollectionFilter = {
  query: '',
  formats: [],
  watchState: 'all',
  loanState: 'all',
  genreIds: [],
  locationIds: [],
  sort: 'title',
  direction: 'asc',
};

/** Leading articles a shelf owner would file under the *next* word. */
const LEADING_ARTICLES = /^(the|a|an|der|die|das|ein|eine|einen|einem|eines|l[ae]s?|el|il)\s+/i;

/**
 * Fold a string down to something comparable: lowercase, no diacritics, no
 * punctuation. "Amélie" matches "amelie", "Straße" matches "strasse".
 */
export function normalize(value: string): string {
  return value
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

/** Title as it would be filed on a shelf: "The Thing" sorts under T-h-i-n-g. */
export function sortableTitle(title: string): string {
  return normalize(title).replace(LEADING_ARTICLES, '');
}

/** Everything about a movie that a search query can hit. */
function haystack(movie: Movie): string {
  const parts: string[] = [movie.title];
  if (movie.titleDe) parts.push(movie.titleDe);
  if (movie.originalTitle) parts.push(movie.originalTitle);
  if (movie.year) parts.push(String(movie.year));
  parts.push(...movie.directors);
  for (const copy of movie.copies) {
    if (copy.edition) parts.push(copy.edition);
    if (copy.loan?.to) parts.push(copy.loan.to);
  }
  if (movie.personalNote) parts.push(movie.personalNote);
  return normalize(parts.join(' '));
}

/** Every whitespace-separated token in the query must appear somewhere. */
export function matchesQuery(movie: Movie, query: string): boolean {
  const tokens = normalize(query).split(' ').filter(Boolean);
  if (tokens.length === 0) return true;
  const target = haystack(movie);
  return tokens.every((token) => target.includes(token));
}

export function displayTitle(movie: Pick<Movie, 'title' | 'titleDe'>, locale: Locale): string {
  if (locale === 'de' && movie.titleDe) return movie.titleDe;
  return movie.title;
}

export function filterMovies(
  movies: Movie[],
  filter: CollectionFilter,
  locations: LocationBook = EMPTY_LOCATIONS,
): Movie[] {
  return movies.filter((movie) => {
    // Where a disc is lives on the copy, and what an unmarked copy counts as
    // lives in the settings, so this one predicate needs both.
    if (!movieIsAtLocation(movie, filter.locationIds, locations)) return false;
    if (filter.formats.length > 0) {
      // The buckets are exclusive: a combo shows under "4K + Full HD" only,
      // never under "4K UHD" as well.
      const bucket = formatBucket(movie);
      if (bucket === null || !filter.formats.includes(bucket)) return false;
    }
    if (filter.watchState === 'watched' && movie.watchCount === 0) return false;
    if (filter.watchState === 'unwatched' && movie.watchCount > 0) return false;
    if (filter.loanState === 'loaned' && loanedCopies(movie).length === 0) return false;
    if (filter.loanState === 'shelf' && isFullyLoanedOut(movie)) return false;
    if (filter.genreIds.length > 0) {
      if (!filter.genreIds.some((id) => movie.genreIds.includes(id))) return false;
    }
    if (!matchesQuery(movie, filter.query)) return false;
    return true;
  });
}

/**
 * Sort in place on a copy of the array.
 *
 * Nulls always sink to the bottom regardless of direction — an unwatched
 * movie is not "the oldest watch", it simply has no answer.
 */
export function sortMovies(
  movies: Movie[],
  sort: SortKey,
  direction: SortDirection,
  locale: Locale,
): Movie[] {
  const collator = new Intl.Collator(locale, { sensitivity: 'base', numeric: true });
  const sign = direction === 'asc' ? 1 : -1;

  const compareTitle = (a: Movie, b: Movie) =>
    collator.compare(
      sortableTitle(displayTitle(a, locale)),
      sortableTitle(displayTitle(b, locale)),
    );

  const byNullableNumber = (a: number | null, b: number | null): number | null => {
    if (a === null && b === null) return null;
    if (a === null) return 1;
    if (b === null) return -1;
    return null;
  };

  return [...movies].sort((a, b) => {
    let result = 0;
    switch (sort) {
      case 'title':
        return sign * compareTitle(a, b);
      case 'added':
        result = a.addedAt.localeCompare(b.addedAt);
        break;
      case 'releaseDate': {
        // Fall back to the year so a film with only a year still sorts
        // sensibly among films that have a full date.
        const left = a.releaseDate ?? (a.year ? `${a.year}-00-00` : null);
        const right = b.releaseDate ?? (b.year ? `${b.year}-00-00` : null);
        if (left === null && right === null) result = 0;
        else if (left === null) return 1;
        else if (right === null) return -1;
        else result = left.localeCompare(right);
        break;
      }
      case 'runtime': {
        const nulls = byNullableNumber(a.runtime, b.runtime);
        if (nulls !== null) return nulls;
        result = (a.runtime ?? 0) - (b.runtime ?? 0);
        break;
      }
      case 'watchCount':
        result = a.watchCount - b.watchCount;
        break;
      case 'lastWatched': {
        if (a.lastWatchedAt === null && b.lastWatchedAt === null) result = 0;
        else if (a.lastWatchedAt === null) return 1;
        else if (b.lastWatchedAt === null) return -1;
        else result = a.lastWatchedAt.localeCompare(b.lastWatchedAt);
        break;
      }
    }
    if (result === 0) return compareTitle(a, b);
    return sign * result;
  });
}

export function filterAndSortMovies(
  movies: Movie[],
  filter: CollectionFilter,
  locale: Locale,
  locations: LocationBook = EMPTY_LOCATIONS,
): Movie[] {
  return sortMovies(filterMovies(movies, filter, locations), filter.sort, filter.direction, locale);
}

/**
 * Rebuild a filter from something untrusted — a stored value from an older
 * version of the app, or one that has been hand-edited.
 *
 * Every field falls back to its default independently, so one unrecognised
 * value costs that field and not the whole filter.
 */
export function parseFilter(value: unknown): CollectionFilter {
  if (typeof value !== 'object' || value === null) return DEFAULT_FILTER;
  const raw = value as Record<string, unknown>;

  const oneOf = <T extends string>(input: unknown, allowed: readonly T[], fallback: T): T =>
    typeof input === 'string' && (allowed as readonly string[]).includes(input)
      ? (input as T)
      : fallback;

  const formats = Array.isArray(raw.formats)
    ? raw.formats.filter((entry): entry is DiscFormat =>
        (DISC_FORMATS as readonly unknown[]).includes(entry),
      )
    : DEFAULT_FILTER.formats;

  const genreIds = Array.isArray(raw.genreIds)
    ? raw.genreIds.filter(
        (entry): entry is number => typeof entry === 'number' && Number.isFinite(entry),
      )
    : DEFAULT_FILTER.genreIds;

  const locationIds = Array.isArray(raw.locationIds)
    ? raw.locationIds.filter((entry): entry is string => typeof entry === 'string')
    : DEFAULT_FILTER.locationIds;

  return {
    query: typeof raw.query === 'string' ? raw.query : DEFAULT_FILTER.query,
    formats: [...new Set(formats)],
    watchState: oneOf<WatchState>(raw.watchState, WATCH_STATES, DEFAULT_FILTER.watchState),
    loanState: oneOf<LoanState>(raw.loanState, LOAN_STATES, DEFAULT_FILTER.loanState),
    genreIds: [...new Set(genreIds)],
    locationIds: [...new Set(locationIds)],
    // 'year' was the old name for this, and may still be sitting in a tab's
    // session storage.
    sort:
      raw.sort === 'year'
        ? 'releaseDate'
        : oneOf<SortKey>(raw.sort, SORT_KEYS, DEFAULT_FILTER.sort),
    direction: oneOf<SortDirection>(raw.direction, SORT_DIRECTIONS, DEFAULT_FILTER.direction),
  };
}

/** How many filters are active, for the "Filters (3)" badge. */
export function activeFilterCount(filter: CollectionFilter): number {
  let count = 0;
  if (filter.formats.length > 0) count += 1;
  if (filter.watchState !== 'all') count += 1;
  if (filter.loanState !== 'all') count += 1;
  if (filter.genreIds.length > 0) count += 1;
  if (filter.locationIds.length > 0) count += 1;
  return count;
}
